/**
 * Modals van de bulkacties op de renovatieticketlijst: toewijzen, fase wijzigen
 * en verwijderen. Gedeeld door beide weergaven van de lijst.
 *
 * @module RenoHelpdesk/Components/RenoBulkActionModals
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { UniversalPickerModal } from 'shared/components/UniversalPickerModal';
import { RenoTicketStatus, STATUS_COLORS } from '../types/renoTicket';

interface RenoBulkActionModalsProps {
  selectedCount: number;
  isSubmitting: boolean;

  isAssignOpen: boolean;
  onAssignOpenChange: (open: boolean) => void;
  onAssign: (user: any) => void;

  isStatusOpen: boolean;
  onStatusOpenChange: (open: boolean) => void;
  onStatusChange: (status: RenoTicketStatus) => Promise<void>;

  isDeleteConfirmOpen: boolean;
  onDeleteConfirmOpenChange: (open: boolean) => void;
  onDelete: () => void;
}

export const RenoBulkActionModals = ({
  selectedCount,
  isSubmitting,
  isAssignOpen,
  onAssignOpenChange,
  onAssign,
  isStatusOpen,
  onStatusOpenChange,
  onStatusChange,
  isDeleteConfirmOpen,
  onDeleteConfirmOpenChange,
  onDelete,
}: RenoBulkActionModalsProps) => (
  <>
    <UniversalPickerModal
      isOpen={isAssignOpen}
      mode="assignee"
      title={`Toewijzen aan (${selectedCount} tickets)`}
      onClose={() => onAssignOpenChange(false)}
      onConfirm={onAssign}
      config={{ allowRemoval: true }}
    />

    <Dialog open={isStatusOpen} onOpenChange={onStatusOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Fase wijzigen ({selectedCount} tickets)</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1.5 py-2">
          {Object.values(RenoTicketStatus)
            .filter((s) => s !== RenoTicketStatus.UNKNOWN)
            .map((s) => (
              <button
                key={s}
                disabled={isSubmitting}
                onClick={async () => {
                  onStatusOpenChange(false);
                  await onStatusChange(s);
                }}
                className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors hover:opacity-80 border ${STATUS_COLORS[s]}`}
              >
                {s}
              </button>
            ))}
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={isDeleteConfirmOpen} onOpenChange={onDeleteConfirmOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Tickets verwijderen</AlertDialogTitle>
          <AlertDialogDescription>
            Ben je zeker dat je {selectedCount} ticket{selectedCount !== 1 ? 's' : ''} wilt
            verwijderen? Dit kan niet ongedaan worden gemaakt. Alle gekoppelde e-mails en notities
            worden ook verwijderd.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuleren</AlertDialogCancel>
          <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">
            Verwijderen
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
);
